'use es6';

import { useState, useEffect, useCallback, useRef } from 'react';
import { PopoverType } from 'rich-text-lib/constants/popover';
import LinkEditPopover from './LinkEditPopover';
import LinkInsertPopover from './LinkInsertPopover';
import useInlineTarget from '../utils/hooks/useInlineTarget';
import { isElementOrChildOf } from '../utils/dom';
import { getLinkAttrProps } from '../utils/link';
import { getAnchorPreviewClickHandler } from '../hslink/shared';
import { isWhitespace } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
function LinkPopoverManager(props) {
  const {
    editor,
    onChangeLink,
    onSelectFile,
    fetchPages,
    fetchMeetingsByUsers,
    fetchAnchors,
    fetchMeetingBySlug,
    fetchPageBySlug,
    fetchFileByUrl,
    showRefAndTargetControls,
    useEmailSubscription,
    allowFileDownload,
    allowMeetingLink,
    allowRawLinksOnly,
    emailSubscriptionLinkTypes,
    allowAnchorLinks,
    removeUnInsertedLinks,
    eventKey,
    allowEmailWebVersionLink
  } = props;
  const {
    inlineTarget,
    setInlineTarget,
    initInlineTarget
  } = useInlineTarget(editor, null);
  const [selectedText, setSelectedText] = useState('');
  const [popoverType, setPopoverType] = useState(PopoverType.INSERT);
  const [selectedLinkAttrs, setSelectedLinkAttrs] = useState(null);
  const lastTarget = useRef(inlineTarget);
  const handleRemove = useCallback(({
    fullRemove,
    restoreSelection
  } = {}) => {
    if (lastTarget.current || fullRemove) {
      setInlineTarget(null, {
        restoreSelection
      });
      setSelectedLinkAttrs(null);
    }
  }, [setInlineTarget]);
  const handleInsertLink = linkInfo => {
    onChangeLink(lastTarget.current, Object.assign({}, linkInfo, {
      allowRawLinksOnly
    }), PopoverType.INSERT);
  };
  const handleEditLink = linkInfo => {
    onChangeLink(lastTarget.current, Object.assign({}, linkInfo, {
      allowRawLinksOnly
    }), PopoverType.EDIT);
  };

  // Store values in immutable refs for useEffect optimization
  useEffect(() => {
    lastTarget.current = inlineTarget;
  });
  useEffect(() => {
    const handleOpenPopover = () => {
      const selectionContent = editor.selection.getContent({
        format: 'text'
      });
      if (lastTarget.current) {
        handleRemove({
          restoreSelection: true
        });
      } else {
        setPopoverType(PopoverType.INSERT);
        if (!isWhitespace(selectionContent) && !allowRawLinksOnly) {
          editor.formatter.apply('link', {
            href: null,
            'data-insert': true
          });
          setSelectedText(selectionContent);
          const parentAnchor = editor.dom.getParent(editor.selection.getSel().anchorNode, 'a');
          setInlineTarget(parentAnchor, {
            removeTargets: true
          });
        } else {
          setSelectedText('');
          initInlineTarget();
        }
      }
    };
    editor.on('OpenLinkPopover', handleOpenPopover);
    return () => editor.off('OpenLinkPopover', handleOpenPopover);
  }, [editor, allowRawLinksOnly, setInlineTarget, initInlineTarget, handleRemove]);
  useEffect(() => {
    const handleHsNodeChange = ({
      element
    }) => {
      if (!element) {
        handleRemove();
        return;
      }
      if (element.hasAttribute('data-mce-inline-target')) {
        return;
      }
      if (element.hasAttribute('data-insert')) {
        return; // Ignore temp data-insert links
      }
      const isNodeWithinActiveLink = isElementOrChildOf(element, lastTarget.current);
      if (element !== lastTarget.current) {
        handleRemove({
          fullRemove: true
        });
        setPopoverType(PopoverType.EDIT);
        setInlineTarget(element);
      } else if (isNodeWithinActiveLink) {
        if (lastTarget.current) {
          editor.fire('ToggleLinkEditPopover', {
            shouldOpen: false
          });
        }
      }
      const linkAttrs = getLinkAttrProps(editor, editor.$(element), allowRawLinksOnly);
      setSelectedLinkAttrs(linkAttrs);
    };
    editor.on(`hsNodeChange:${eventKey}`, handleHsNodeChange);
    return () => editor.off(`hsNodeChange:${eventKey}`, handleHsNodeChange);
  }, [editor, eventKey, allowRawLinksOnly, setInlineTarget, handleRemove]);
  useEffect(() => {
    if (allowRawLinksOnly) {
      // For `allowRawLinksOnly` we need to update the link attributes on every
      // keystroke to make sure the link text + href are kept in sync.
      const handleKeyUp = () => {
        if (lastTarget.current) {
          const linkAttrs = getLinkAttrProps(editor, editor.$(lastTarget.current), allowRawLinksOnly);
          setSelectedLinkAttrs(linkAttrs);
        }
      };
      editor.on('KeyUp', handleKeyUp);
      return () => editor.off('KeyUp', handleKeyUp);
    }
    return undefined;
  }, [editor, allowRawLinksOnly]);
  if (popoverType === PopoverType.INSERT) {
    return /*#__PURE__*/_jsx(LinkInsertPopover, {
      editor: editor,
      open: !!inlineTarget,
      target: inlineTarget,
      onRemove: handleRemove,
      fetchPages: fetchPages,
      fetchPageBySlug: fetchPageBySlug,
      fetchFileByUrl: fetchFileByUrl,
      fetchMeetingsByUsers: fetchMeetingsByUsers,
      fetchMeetingBySlug: fetchMeetingBySlug,
      fetchAnchors: fetchAnchors,
      onSelectFile: onSelectFile,
      showRefAndTargetControls: showRefAndTargetControls,
      useEmailSubscription: useEmailSubscription,
      allowFileDownload: allowFileDownload,
      allowMeetingLink: allowMeetingLink,
      allowRawLinksOnly: allowRawLinksOnly,
      emailSubscriptionLinkTypes: emailSubscriptionLinkTypes,
      allowAnchorLinks: allowAnchorLinks,
      selectedText: selectedText,
      mountedToText: true,
      canModifyLinkText: isWhitespace(selectedText) && !allowRawLinksOnly,
      onInsert: handleInsertLink,
      removeUnInsertedLinks: removeUnInsertedLinks,
      allowEmailWebVersionLink: allowEmailWebVersionLink
    });
  }
  if (popoverType === PopoverType.EDIT && selectedLinkAttrs !== null) {
    return /*#__PURE__*/_jsx(LinkEditPopover, Object.assign({
      editor: editor,
      open: !!inlineTarget,
      target: inlineTarget,
      onRemove: handleRemove,
      fetchPages: fetchPages,
      fetchMeetingsByUsers: fetchMeetingsByUsers,
      fetchAnchors: fetchAnchors,
      fetchMeetingBySlug: fetchMeetingBySlug,
      onSelectFile: onSelectFile,
      showRefAndTargetControls: showRefAndTargetControls,
      useEmailSubscription: useEmailSubscription,
      allowFileDownload: allowFileDownload,
      allowMeetingLink: allowMeetingLink,
      allowRawLinksOnly: allowRawLinksOnly,
      emailSubscriptionLinkTypes: emailSubscriptionLinkTypes,
      allowAnchorLinks: allowAnchorLinks,
      fetchPageBySlug: fetchPageBySlug,
      fetchFileByUrl: fetchFileByUrl,
      onUpdate: handleEditLink,
      onAnchorPreviewClick: getAnchorPreviewClickHandler(editor),
      onClose: () => editor.focus(),
      allowEmailWebVersionLink: allowEmailWebVersionLink
    }, selectedLinkAttrs));
  }
  return null;
}
export default LinkPopoverManager;